<template>
  <v-card id="map_card">
    <v-toolbar
      v-if="isPopup"
      height="50px"
      class="sub-header"
      color="primary darken-1"
    >
      <v-toolbar-title class="body-2">{{ $t("Map View") }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-icon @click="$emit('close')" :dark="isDark">close</v-icon>
    </v-toolbar>
    <v-container pa-0>
      <v-layout wrap>
        <v-flex xs12>
          <div id="popMap" ref="mapContainer"></div>
        </v-flex>
      </v-layout>
    </v-container>
  </v-card>
</template>

<script>
import moment from 'moment-timezone';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { mapGetters, mapMutations } from "vuex";
import { mapGettersMixin } from "@/util/mixin";
import PlantOverviewApi from "@/services/PlantOverview";

const MAP_ACCESS_TOKEN = process.env.VUE_APP_MAPBOX_MAP_KEY;
const DEFAULT_CENTER = { lat: -12.270972, lng: 79.890688 };
const DEFAULT_ZOOM = 2;
const SPECIAL_PLANT_IDS = [109, 145, 146, 173];
const UNIT_MAP = {
  generation: "kWh",
  pr: "%",
  cuf: "%",
};

// Marker configuration by device type
const DEVICE_CONFIG = {
  "inverter room": {
    state: "inverterRoomGeneration",
    level: ["blockId", "id"],
    stableIcon: require("../../assets/markers/inv-stable.png"),
    alertIcon: require("../../assets/markers/inv-alert.png"),
    route: "inverter"
  },
  "inverter": {
    state: "inverterGeneration",
    level: ["blockId", "roomId", "id"],
    stableIcon: require("../../assets/markers/inv-stable.png"),
    alertIcon: require("../../assets/markers/inv-alert.png"),
    route: "inverter"
  },
  "scb": {
    state: "scbCurrent",
    level: ["blockId", "roomId", "inverterId", "id"],
    stableIcon: require("../../assets/markers/scb-stable.png"),
    alertIcon: require("../../assets/markers/scb-alert.png"),
    route: "advancedScb"
  }
};

export default {
  name: 'MapView',
  mixins: [mapGettersMixin],
  props: {
    isPopup: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      map: null,
      markerElements: [],
      visibleLayers: {
        scb: true,
        invRoom: true,
        inv: false
      },
      mapData: {
        scb: [],
        invRoom: [],
        inv: []
      },
      deviceData: {
        inverterRoomGeneration: {},
        inverterGeneration: {},
        scbCurrent: {}
      },
      controls: {
        filterButtons: null,
        legend: null
      },
      hoveringOnAlert: false,
      dataFetchInterval: null
    };
  },
  computed: {
    ...mapGetters(["getActivePlantTimezone", "getSelectedPlantId"]),
    
    visibleMarkers() {
      let markers = [];
      
      if (this.visibleLayers.scb && this.mapData.scb.length > 0) {
        markers = [...markers, ...this.mapData.scb];
      }
      
      if (this.visibleLayers.invRoom && this.mapData.invRoom.length > 0) {
        markers = [...markers, ...this.mapData.invRoom];
      }
      
      if (this.visibleLayers.inv && this.mapData.inv.length > 0) {
        markers = [...markers, ...this.mapData.inv];
      }
      
      return markers;
    }
  },
  mounted() {
    mapboxgl.accessToken = MAP_ACCESS_TOKEN;
    this.initMap();
    this.dataFetchInterval = this.setIntervalWithoutDelay(this.fetchMapData);
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
    }
    
    if (this.dataFetchInterval) {
      clearInterval(this.dataFetchInterval);
    }
    
    // Clean up filter buttons if they exist
    if (this.controls.filterButtons && this.controls.filterButtons.parentNode) {
      this.controls.filterButtons.parentNode.removeChild(this.controls.filterButtons);
    }
  },
  methods: {
    ...mapMutations([
      "SET_SELECTED_BLOCK_ID",
      "SET_MAP_REDIRECT_PLANT",
      "SET_MAP_REDIRECT_BLOCK",
      "SET_SHOW_SNACKBAR",
      "SET_SNACKBAR_CONTENT",
    ]),
    initMap() {
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: "mapbox://styles/etitsupport/cm99y24p3000e01sb03nqg4sy",
        center: [DEFAULT_CENTER.lng, DEFAULT_CENTER.lat],
        zoom: DEFAULT_ZOOM,
        pitch: 45,
      });

      this.map.on('load', () => {
        this.initMapControls();
      });
    },
    initMapControls() {
      this.addFilterButtons();
      this.addLegendControl();
    },
    addFilterButtons() {
      const container = document.createElement('div');
      container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
      Object.assign(container.style, {
        display: 'flex',
        justifyContent: 'center',
        gap: '5px',
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: '1',
        backgroundColor: 'rgba(255, 255, 255, 0)',
      });
      
      const createFilterButton = (id, label, isActive = false) => {
        const button = document.createElement('button');
        Object.assign(button.style, {
          backgroundColor: 'white',
          minWidth: '65px',
          border: 'none',
          cursor: 'pointer',
          padding: '7px 15px',
          borderRadius: '3px',
          fontFamily: 'Roboto, Arial, sans-serif',
          fontWeight: '500',
          fontSize: '16px',
          color: isActive ? 'black' : 'gray',
          textAlign: 'center'
        });
        
        button.id = id;
        button.textContent = this.$t(label);
        button.title = this.$t(label);
        
        return button;
      };
      
      // Add SCB button if markers exist
      if (this.mapData.scb.length > 0) {
        const scbButton = createFilterButton('scb-button', 'SCB', this.visibleLayers.scb);
        scbButton.addEventListener('click', () => {
          this.visibleLayers.scb = !this.visibleLayers.scb;
          scbButton.style.color = this.visibleLayers.scb ? 'black' : 'gray';
        });
        container.appendChild(scbButton);
      }
      
      // Add inverter room button if markers exist
      if (this.mapData.invRoom.length > 0) {
        const invRoomButton = createFilterButton('inv-room-button', 'INV ROOM', this.visibleLayers.invRoom);
        invRoomButton.addEventListener('click', () => {
          this.visibleLayers.invRoom = !this.visibleLayers.invRoom;
          invRoomButton.style.color = this.visibleLayers.invRoom ? 'black' : 'gray';
          
          if (this.visibleLayers.invRoom) {
            this.visibleLayers.inv = false;
            const invButton = document.getElementById('inv-button');
            if (invButton) invButton.style.color = 'gray';
          }
        });
        container.appendChild(invRoomButton);
      }
      
      // Add inverter button if markers exist
      if (this.mapData.inv.length > 0) {
        const invButton = createFilterButton('inv-button', 'INV', this.visibleLayers.inv);
        invButton.addEventListener('click', () => {
          this.visibleLayers.inv = !this.visibleLayers.inv;
          invButton.style.color = this.visibleLayers.inv ? 'black' : 'gray';
          
          if (this.visibleLayers.inv) {
            this.visibleLayers.invRoom = false;
            const invRoomButton = document.getElementById('inv-room-button');
            if (invRoomButton) invRoomButton.style.color = 'gray';
          }
        });
        container.appendChild(invButton);
      }
      
      this.$refs.mapContainer.appendChild(container);
      this.controls.filterButtons = container;
    },
    addLegendControl() {
      class LegendControl {
        constructor(options) {
          this._options = options || {};
        }
        
        onAdd(map) {
          this._map = map;
          this._container = document.createElement('div');
          this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
          
          Object.assign(this._container.style, {
            marginTop: '10px',
            padding: '9px'
          });
          
          this._addLegendItem(this._options.scbIcon, 'SCB');
          this._addLegendItem(this._options.invIcon, 'Inverter / Room');
          
          return this._container;
        }
        
        onRemove() {
          this._container.parentNode.removeChild(this._container);
          this._map = undefined;
        }
        
        _addLegendItem(iconSrc, label) {
          const container = document.createElement('div');
          container.style.marginBottom = '5px';
          
          const image = document.createElement('img');
          Object.assign(image.style, {
            height: '24px',
            width: '24px',
            display: 'inline',
            verticalAlign: 'middle'
          });
          image.src = iconSrc;
          container.appendChild(image);
          
          const text = document.createElement('span');
          text.textContent = label;
          Object.assign(text.style, {
            display: 'inline-block',
            marginLeft: '5px',
            color: 'black',
            verticalAlign: 'middle'
          });
          container.appendChild(text);
          
          this._container.appendChild(container);
        }
      }
      
      this.map.addControl(new LegendControl({
        scbIcon: DEVICE_CONFIG.scb.stableIcon,
        invIcon: DEVICE_CONFIG["inverter room"].stableIcon
      }), 'top-right');
    },
    async fetchMapData() {
      try {
        const date = moment().tz(this.getActivePlantTimezone).format('YYYY-MM-DD');
        
        // Reset state for new data
        this.resetDataState();
        
        const [invRoomGen, invGen, scbCurrent, coordinates] = await Promise.all([
          this.fetchDeviceData('inverterRoomGeneration', date),
          this.fetchDeviceData('inverterGeneration', date),
          this.fetchDeviceData('scbCurrent', date),
          this.fetchCoordinates(date)
        ]);
        
        this.deviceData.inverterRoomGeneration = invRoomGen;
        this.deviceData.inverterGeneration = invGen;
        this.deviceData.scbCurrent = scbCurrent;
        
        if (coordinates) {
          this.mapData.invRoom = coordinates.inverterRoomCoordinates || [];
          this.mapData.scb = coordinates.scbCoordinates || [];
          this.mapData.inv = coordinates.inverterCoordinates || [];
        }
        
        // Update visibility if markers changed
        this.updateLayerVisibility();
        
        // Refresh map markers
        this.renderMarkers();
        
        // Add controls if not already added
        if (!this.controls.filterButtons) {
          this.addFilterButtons();
        }
      } catch (err) {
        this.handleError(err);
      }
    },
    
    resetDataState() {
      // Reset device data
      Object.keys(this.deviceData).forEach(key => {
        this.deviceData[key] = {};
      });
    },
    
    async fetchDeviceData(endpoint, date) {
      try {
        const response = await PlantOverviewApi[`get${endpoint.charAt(0).toUpperCase() + endpoint.slice(1)}`](
          this.getSelectedPlantId,
          date,
          date
        );
        
        return Object.keys(response.data.message).length > 0 
          ? response.data.message 
          : this.$t("No data");
      } catch (err) {
        console.error(`Error fetching ${endpoint}:`, err);
        return this.$t("No data");
      }
    },
    
    async fetchCoordinates(date) {
      try {
        const response = await PlantOverviewApi.plantCoordinates(
          this.getSelectedPlantId,
          date,
          date
        );
        return response.data.message;
      } catch (err) {
        console.error("Error fetching coordinates:", err);
        return null;
      }
    },
    updateLayerVisibility() {
      if (this.mapData.scb.length === 0 && this.visibleLayers.scb) {
        this.visibleLayers.scb = false;
      } else if (this.mapData.scb.length > 0 && !this.visibleLayers.scb) {
        this.visibleLayers.scb = true;
      }
      
      if (this.mapData.invRoom.length === 0 && this.visibleLayers.invRoom) {
        this.visibleLayers.invRoom = false;
      }
      
      this.updateFilterButtons();
    },
    updateFilterButtons() {
      const scbButton = document.getElementById('scb-button');
      const invRoomButton = document.getElementById('inv-room-button');
      const invButton = document.getElementById('inv-button');
      
      if (scbButton) {
        scbButton.style.display = this.mapData.scb.length > 0 ? 'inline-block' : 'none';
        scbButton.style.color = this.visibleLayers.scb ? 'black' : 'gray';
      }
      
      if (invRoomButton) {
        invRoomButton.style.display = this.mapData.invRoom.length > 0 ? 'inline-block' : 'none';
        invRoomButton.style.color = this.visibleLayers.invRoom ? 'black' : 'gray';
      }
      
      if (invButton) {
        invButton.style.display = this.mapData.inv.length > 0 ? 'inline-block' : 'none';
        invButton.style.color = this.visibleLayers.inv ? 'black' : 'gray';
      }
    },
    
    renderMarkers() {
      this.clearMarkers();
      
      if (this.visibleMarkers.length === 0) return;
      
      if (this.visibleMarkers.length === 1) {
        this.map.setZoom(15);
      }
      
      const bounds = new mapboxgl.LngLatBounds();
      
      this.visibleMarkers.forEach((marker, idx) => {
        if (this.isValidPosition(marker.position)) {
          const el = this.createMarkerElement(marker);
          
          const mapboxMarker = new mapboxgl.Marker(el)
            .setLngLat([marker.position.lng, marker.position.lat])
            .addTo(this.map);
          
          const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false
          });
          
          this.addMarkerEventListeners(el, marker, popup, mapboxMarker);
          
          this.markerElements.push(mapboxMarker);
          
          bounds.extend([marker.position.lng, marker.position.lat]);
        }
      });
      
      // Fit map to bounds if there are valid markers
      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds, { padding: 50 });
      }
    },
    
    clearMarkers() {
      this.markerElements.forEach(marker => marker.remove());
      this.markerElements = [];
    },
    
    isValidPosition(position) {
      return position && 
        position.lat != null && 
        position.lng != null && 
        !isNaN(position.lat) && 
        !isNaN(position.lng);
    },
    createMarkerElement(marker) {
      const el = document.createElement('div');
      el.className = 'marker';
      
      Object.assign(el.style, {
        backgroundImage: `url(${this.getMarkerIcon(marker)})`,
        width: '30px',
        height: '30px',
        backgroundSize: '100%',
        cursor: 'pointer'
      });
      
      if (marker.alert && !this.hoveringOnAlert) {
        el.style.animation = 'bounce 1s infinite';
      }
      
      return el;
    },
    addMarkerEventListeners(el, marker, popup, mapboxMarker) {
      el.addEventListener('mouseenter', () => {
        if (marker.alert) {
          this.hoveringOnAlert = true;
        }
        
        const popupContent = this.createPopupContent(marker);
        popup.setLngLat([marker.position.lng, marker.position.lat])
          .setHTML(popupContent)
          .addTo(this.map);
      });
      
      el.addEventListener('mouseleave', () => {
        this.hoveringOnAlert = false;
        popup.remove();
      });
      
      el.addEventListener('click', () => {
        this.navigateToDeviceDashboard(marker);
      });
    },
    
    getMarkerIcon(marker) {
      const config = DEVICE_CONFIG[marker.device];
      if (!config) return '';
      
      return marker.alert ? config.alertIcon : config.stableIcon;
    },
    
    createPopupContent(marker) {
      let title = marker.name;
      if (SPECIAL_PLANT_IDS.includes(this.getSelectedPlantId)) {
        title = `B-${marker.blockId} , ${marker.name}`;
      }
      
      const deviceConfig = DEVICE_CONFIG[marker.device];
      if (!deviceConfig) return `<div style="color: black;">${title}<br>No data</div>`;
      
      let content = `<div style="color: black;">${title}`;
      
      const deviceData = this.deviceData[deviceConfig.state];
      
      if (typeof deviceData === 'object' && Object.keys(deviceData).length === 0) {
        return `${content}<br>Loading...</div>`;
      } else if (typeof deviceData === 'string' && deviceData === this.$t("No data")) {
        return `${content}<br>No data</div>`;
      }
      
      try {
        let targetData = JSON.parse(JSON.stringify(deviceData)); // Clean deep clone
        let dataFound = true;
        
        for (const key of deviceConfig.level) {
          if (targetData && targetData[marker[key]]) {
            targetData = targetData[marker[key]];
          } else {
            dataFound = false;
            break;
          }
        }
        
        content += '<br>';
        if (dataFound && Object.keys(targetData).length > 0) {
          Object.entries(targetData).forEach(([key, value]) => {
            const unit = UNIT_MAP[key] || '';
            content += `<br><b>${this.$t(key)}: </b> ${value}${unit}`;
          });
        } else {
          content += '<br>No data';
        }
      } catch (err) {
        console.error('Error creating popup content:', err);
        content += '<br>Error loading data';
      }
      
      return `${content}</div>`;
    },
    navigateToDeviceDashboard(marker) {
      this.SET_SELECTED_BLOCK_ID(marker.blockId);
      
      if (marker.device === 'inverter room' || marker.device === 'inverter') {
        this.SET_MAP_REDIRECT_PLANT(true);
        this.SET_MAP_REDIRECT_BLOCK(true);
        this.$router.push({ name: 'inverter' });
      } else if (marker.device === 'scb') {
        this.$router.push({ name: 'advancedScb' });
      }
    },
    handleError(error) {
      console.error('Map error:', error);
      const payload = {
        icon: 'error',
        header: '',
        message: error.toString()
      };
      this.SET_SNACKBAR_CONTENT(payload);
      this.SET_SHOW_SNACKBAR(true);
    }
  },
  watch: {
    visibleLayers: {
      handler() {
        this.renderMarkers();
      },
      deep: true
    },
    getSelectedPlantId() {
      this.fetchMapData();
    }
  }
};
</script>

<style scoped>
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
</style>

<style>
#popMap {
  width: 100%; 
  height: 400px;
}
.mapboxgl-popup-content {
  padding: 10px;
  border-radius: 4px;
}
</style>